/*!
 * mm-mixer.js — plays the mashup and renders it to a file, from ONE graph.
 *
 *   var mix = MashupMixer.create({ ctx, workletUrl });
 *   mix.setTracks({ vocal: {buffer, at}, vocal2?: {buffer, at}, stems: { 'b:drums': {buffer, at, segs?}, 'a:bass': …, … }, duration })
 *     segs — the track as CLIPS of its buffer, [{at, from, dur}] on the timeline: the music going round
 *     under a vocal longer than it (MashupPlan.musicLoop) is its own buffer played again, not a copy
 *     vocal2 — the second singer of a three-song mashup: the SAME vocal chain (one room, one ducking
 *     key), its own switch and level under 'c:vocals'
 *   mix.set({ vocalDb, reverb (0…1), duck (0…1), gains: {'b:drums': 1, …}, on: {'b:vocals': false, …}, fade })
 *   mix.play(fromSec) / pause() / position() / isPlaying() / onTick(fn) / onEnd(fn)
 *   mix.render({ loud: 'match'|'stream'|'club', matchLufs, onProgress }) → AudioBuffer (final, limited)
 *   MashupMixer.layTrack(track, out, sr) — the same clips and seams written into a buffer (the stems pack)
 *   MashupMixer.trackEnd(track) — where a track stops sounding on the timeline
 *
 * The vocal chain: high-pass 100 Hz (a separated vocal carries kick and bass
 * leftovers below that — DJ TechTools "high-pass the acapella around 80–150 Hz"),
 * +2 dB presence at 3.5 kHz, then a send to a plate-like reverb returned through
 * 300 Hz – 7 kHz (Sound On Sound's plate recipe: the reverb glues two unrelated
 * recordings and hides separation leftovers). The music bus goes through
 * mm-duck-worklet (a dip in the vocal range only while the vocal sounds).
 *
 * Preview and export build the SAME graph — the export in an OfflineAudioContext —
 * so the file is what was heard; the export then gets loudness and a true-peak
 * limiter (MasteringLimiter, −1 dBTP) that a live preview cannot afford.
 */
(function (root) {
    'use strict';

    var OC = root.OfflineAudioContext || root.webkitOfflineAudioContext;

    /** A stereo plate-like impulse: decorrelated noise, exponential decay, darker as it decays. */
    function plateIR(ctx, seconds, predelay) {
        var sr = ctx.sampleRate, n = Math.floor(sr * (seconds + predelay)), pd = Math.floor(sr * predelay);
        var ir = ctx.createBuffer(2, n, sr), seed = 1;
        function rnd() { seed = (seed * 16807) % 2147483647; return seed / 2147483647 * 2 - 1; }
        for (var c = 0; c < 2; c++) {
            var d = ir.getChannelData(c), lp = 0;
            for (var i = pd; i < n; i++) {
                var t = (i - pd) / sr, decay = Math.pow(10, -3 * t / seconds); // −60 dB at `seconds`
                var damp = Math.min(0.95, 0.15 + t * 0.9);                       // high frequencies die first
                lp = lp + (1 - damp) * (rnd() - lp);
                d[i] = lp * decay;
            }
        }
        return ir;
    }

    function buildGraph(ctx, dest, st, useWorklet) {
        var g = { sources: [], nodes: {} };
        var master = ctx.createGain(); master.gain.value = 1; master.connect(dest);
        var musicBus = ctx.createGain();
        var duck = null;
        if (useWorklet && st.duck > 0) {
            try {
                duck = new AudioWorkletNode(ctx, 'mashup-duck', { numberOfInputs: 2, numberOfOutputs: 1, outputChannelCount: [2] });
                duck.parameters.get('depth').value = 3 * st.duck;
                duck.parameters.get('broad').value = 1 * st.duck;
                musicBus.connect(duck, 0, 0);
                duck.connect(master);
            } catch (_) { duck = null; }
        }
        if (!duck) musicBus.connect(master);

        // vocal chain
        var hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 100; hp.Q.value = 0.707;
        var pres = ctx.createBiquadFilter(); pres.type = 'peaking'; pres.frequency.value = 3500; pres.Q.value = 1; pres.gain.value = 2;
        var vGain = ctx.createGain(); vGain.gain.value = Math.pow(10, (st.vocalDb || 0) / 20);
        hp.connect(pres); pres.connect(vGain); vGain.connect(master);
        if (duck) vGain.connect(duck, 0, 1);
        if (st.reverb > 0) {
            var send = ctx.createGain(); send.gain.value = 0.35 * st.reverb;
            var conv = ctx.createConvolver(); conv.normalize = true; conv.buffer = plateIR(ctx, 1.7, 0.03);
            var rhp = ctx.createBiquadFilter(); rhp.type = 'highpass'; rhp.frequency.value = 300;
            var rlp = ctx.createBiquadFilter(); rlp.type = 'lowpass'; rlp.frequency.value = 7000;
            vGain.connect(send); send.connect(conv); conv.connect(rhp); rhp.connect(rlp); rlp.connect(master);
        }
        g.vocalIn = hp; g.musicIn = musicBus; g.master = master; g.vGain = vGain;
        return g;
    }

    // ── a track as clips ─────────────────────────────────────────────────────────────────
    // Where the music jumps back to repeat its body, the outgoing clip plays on past the seam and the
    // incoming one starts before it, crossing over XF — each as far as its buffer has sound there (the
    // body that starts on the file's first sample has nothing before it: that side is a cut). A seam on
    // a bar line of a beat is a rhythmic match; the crossover only keeps a held bass note from clicking.
    var XF = 0.008;

    /** [{t0, t1, off, fin, fout}]: the timeline span each clip sounds, its buffer offset at t0, and its
     *  fade-in / fade-out windows (null — none). A plain track is one clip: its buffer from `at`. */
    function clipsOf(tr) {
        var buf = tr && tr.buffer;
        if (!buf) return [];
        var bd = buf.duration, h = XF / 2, out = [];
        var segs = tr.segs && tr.segs.length ? tr.segs : [{ at: tr.at || 0, from: 0, dur: bd }];
        for (var i = 0; i < segs.length; i++) {
            var g = segs[i], from = Math.max(0, Math.min(bd, g.from)), dur = Math.max(0, Math.min(g.dur, bd - from));
            if (!(dur > 0)) continue;
            var c = { t0: g.at, t1: g.at + dur, off: from, fin: null, fout: null, end: from + dur }, pv = out[out.length - 1];
            if (pv && Math.abs(pv.t1 - c.t0) < 1e-6 && Math.abs(pv.end - c.off) > 1e-6) {
                var a = Math.min(h, c.off), b = Math.min(h, bd - pv.end);
                if (a + b > 1e-4) {
                    var w = [c.t0 - a, c.t0 + b];
                    c.t0 -= a; c.off -= a; c.fin = w;
                    pv.t1 += b; pv.fout = w;
                } else {
                    // no sound past either side (a piece cut exactly to its body): a dip of h + h, not a click
                    pv.fout = [pv.t1 - h, pv.t1]; c.fin = [c.t0, c.t0 + h];
                }
            }
            out.push(c);
        }
        return out;
    }
    function envAt(c, t) {
        var g = 1;
        if (c.fin && t < c.fin[1]) g *= Math.max(0, Math.min(1, (t - c.fin[0]) / (c.fin[1] - c.fin[0])));
        if (c.fout && t > c.fout[0]) g *= Math.max(0, Math.min(1, (c.fout[1] - t) / (c.fout[1] - c.fout[0])));
        return g;
    }
    function trackEnd(tr) { var cs = clipsOf(tr), e = tr && tr.buffer ? (tr.at || 0) : 0; cs.forEach(function (c) { if (c.t1 > e) e = c.t1; }); return e; }

    /** Every clip of a track that still sounds from timeline position `from`, started at context time `when`. */
    function startTrack(ctx, tr, dest, from, when, gain) {
        var out = [];
        if (gain == null) gain = 1;
        clipsOf(tr).forEach(function (c) {
            if (c.t1 <= from + 1e-6) return;
            var s0 = Math.max(c.t0, from), off = c.off + (s0 - c.t0);
            if (off >= tr.buffer.duration) return;
            var src = ctx.createBufferSource(); src.buffer = tr.buffer;
            var node = src, faded = !!(c.fin || c.fout), at = function (t) { return when + (t - from); };
            if (faded || gain !== 1) {
                var gg = ctx.createGain(), gp = gg.gain;
                gp.setValueAtTime(gain * envAt(c, s0), at(s0));
                if (c.fin && c.fin[1] > s0) gp.linearRampToValueAtTime(gain, at(c.fin[1]));
                if (c.fout) {
                    var f0 = Math.max(c.fout[0], s0);
                    if (f0 > s0) gp.setValueAtTime(gain * envAt(c, f0), at(f0));
                    gp.linearRampToValueAtTime(0, at(c.fout[1]));
                }
                src.connect(gg); node = gg;
            }
            node.connect(dest);
            src.start(at(s0), off, c.t1 - s0);
            out.push(src);
        });
        return out;
    }

    /** The track's clips written into `out` (an AudioBuffer from timeline 0:00) with the same seams the player makes. */
    function layTrack(tr, out, sr) {
        var buf = tr.buffer, nOut = out.length, nc = buf.numberOfChannels, bsr = buf.sampleRate;
        clipsOf(tr).forEach(function (c) {
            var i0 = Math.max(0, Math.ceil(c.t0 * sr)), i1 = Math.min(nOut, Math.ceil(c.t1 * sr)), faded = !!(c.fin || c.fout);
            for (var ch = 0; ch < out.numberOfChannels; ch++) {
                var src = buf.getChannelData(Math.min(ch, nc - 1)), dst = out.getChannelData(ch);
                for (var i = i0; i < i1; i++) {
                    var t = i / sr, k = Math.round((c.off + (t - c.t0)) * bsr);
                    if (k < 0 || k >= src.length) continue;
                    dst[i] += faded ? src[k] * envAt(c, t) : src[k];
                }
            }
        });
        return out;
    }

    function create(opts) {
        var ctx = opts.ctx, workletUrl = opts.workletUrl, workletReady = null;
        var tracks = { vocal: null, stems: {}, duration: 0 };
        var st = { vocalDb: 0, reverb: 0.35, duck: 1, gains: {}, on: {}, fade: true };
        var live = null, t0Ctx = 0, t0Pos = 0, playing = false, tickFn = null, endFn = null, raf = 0, loop = null, version = 0;
        // play() waits for the context and the worklet (seconds on a phone): each call takes a number,
        // and only the latest one may build the graph. Without it a second tap during the wait built a
        // second graph over the same `live` slot — the first kept sounding, unreachable by pause() —
        // and a pause() during the wait was ignored (QA 2026-09-24). `pending` is that wait: the button
        // already reads «Pause», so a second tap stops instead of starting again.
        var playSeq = 0, pending = 0;

        function ensureWorklet(c) {
            if (!workletUrl || !c.audioWorklet) return Promise.resolve(false);
            if (c === ctx) {
                if (!workletReady) workletReady = c.audioWorklet.addModule(workletUrl).then(function () { return true; }, function () { return false; });
                return workletReady;
            }
            return c.audioWorklet.addModule(workletUrl).then(function () { return true; }, function () { return false; });
        }

        function stemGain(key) { return st.on[key] === false ? 0 : (st.gains[key] != null ? st.gains[key] : 1); }

        function stopLive() {
            if (!live) return;
            live.sources.forEach(function (s) { try { s.stop(); } catch (_) {} try { s.disconnect(); } catch (_) {} });
            try { live.master.disconnect(); } catch (_) {}
            live = null;
        }
        function position() {
            if (!playing) return t0Pos;
            return t0Pos + (ctx.currentTime - t0Ctx);
        }
        function tick() {
            if (!playing) return;
            var p = position();
            if (loop && p >= loop[1]) { play(loop[0]); return; }
            if (p >= tracks.duration) { pause(); t0Pos = 0; if (endFn) endFn(); return; }
            if (tickFn) tickFn(p);
            raf = root.requestAnimationFrame(tick);
        }

        async function play(from) {
            var my = ++playSeq;
            from = Math.max(0, Math.min(from == null ? t0Pos : from, tracks.duration));
            stopLive();
            pending = my;
            if (ctx.state === 'suspended') { try { await ctx.resume(); } catch (_) {} }
            var ok = await ensureWorklet(ctx);
            if (my !== playSeq) return false;                 // a newer play() or a pause() came meanwhile
            pending = 0;
            stopLive();
            live = buildGraph(ctx, ctx.destination, st, ok);
            var when = ctx.currentTime + 0.05;
            [['vocal', 'a:vocals'], ['vocal2', 'c:vocals']].forEach(function (vk) {
                var tr0 = tracks[vk[0]];
                if (!tr0 || !(stemGain(vk[1]) > 0)) return;
                Array.prototype.push.apply(live.sources, startTrack(ctx, tr0, live.vocalIn, from, when, stemGain(vk[1])));
            });
            Object.keys(tracks.stems).forEach(function (k) {
                var gk = stemGain(k);
                if (!gk) return;
                Array.prototype.push.apply(live.sources, startTrack(ctx, tracks.stems[k], live.musicIn, from, when, gk));
            });
            t0Ctx = when; t0Pos = from; playing = true;
            root.cancelAnimationFrame(raf); raf = root.requestAnimationFrame(tick);
            return true;
        }
        function pause() {
            playSeq++; pending = 0;
            if (playing) t0Pos = position();
            playing = false; root.cancelAnimationFrame(raf); stopLive();
        }

        /** Re-apply parameters to a running preview: rebuild from the current position (glitch-free enough for knobs). */
        function set(next) {
            Object.keys(next || {}).forEach(function (k) {
                if (k === 'gains' || k === 'on') st[k] = Object.assign({}, st[k], next[k]); else st[k] = next[k];
            });
            if (playing) play(position());
        }

        async function render(ro) {
            ro = ro || {};
            // ro.range [t0, t1]: only that stretch of the timeline (a short clip for a video); the
            // sources start part-way in exactly as the player does when it seeks there
            var r0 = 0, r1 = tracks.duration;
            if (ro.range && ro.range[1] > ro.range[0]) { r0 = Math.max(0, ro.range[0]); r1 = Math.min(tracks.duration, ro.range[1]); }
            var span = Math.max(1 / ctx.sampleRate, r1 - r0);
            var sr = ctx.sampleRate, n = Math.ceil(span * sr);
            var off = new OC(2, n, sr);
            var ok = await ensureWorklet(off);
            // ro.on overrides which stems sound IN THIS RENDER only (the music file without the vocals):
            // flipping the live state instead restarted a playing preview without its vocal
            var S = ro.on ? Object.assign({}, st, { on: Object.assign({}, st.on, ro.on) }) : st;
            var gainOf = function (key) { return S.on[key] === false ? 0 : (S.gains[key] != null ? S.gains[key] : 1); };
            var g = buildGraph(off, off.destination, S, ok);
            [['vocal', 'a:vocals'], ['vocal2', 'c:vocals']].forEach(function (vk) {
                var tr0 = tracks[vk[0]];
                if (tr0 && gainOf(vk[1]) > 0) startTrack(off, tr0, g.vocalIn, r0, 0, gainOf(vk[1]));
            });
            Object.keys(tracks.stems).forEach(function (k) {
                var gk = gainOf(k);
                if (gk) startTrack(off, tracks.stems[k], g.musicIn, r0, 0, gk);
            });
            if (S.fade) {
                var fin = Math.min(2, span / 10), fout = Math.min(4, span / 8);
                g.master.gain.setValueAtTime(0, 0);
                g.master.gain.linearRampToValueAtTime(1, fin);
                g.master.gain.setValueAtTime(1, Math.max(fin, span - fout));
                g.master.gain.linearRampToValueAtTime(0, span);
            }
            // OfflineAudioContext has no progress event: suspend at 5 % steps to report one (a
            // 3:43 master is ~40 s on a desktop and minutes on a phone — a static label reads as a hang)
            if (ro.onProgress && off.suspend) {
                var q = 128 / sr, seen = {};
                for (var step = 1; step < 20; step++) {
                    var at = Math.floor(step * span / 20 / q) * q;
                    if (at <= 0 || at >= span || seen[at]) continue;
                    seen[at] = 1;
                    (function (tAt) { off.suspend(tAt).then(function () { try { ro.onProgress(0.6 * tAt / span); } catch (_) {} off.resume(); }); })(at);
                }
            }
            var tm = { t0: performance.now() };
            if (ro.onProgress) ro.onProgress(0);
            var out = await off.startRendering();
            tm.render = performance.now();
            if (ro.onProgress) ro.onProgress(0.6);
            // loudness: the music song's own level by default, a streaming or club target on request
            var target = ro.loud === 'stream' ? -14 : ro.loud === 'club' ? -9 : (isFinite(ro.matchLufs) ? ro.matchLufs : -12);
            target = Math.max(-18, Math.min(-8, target));
            var L = root.LoudnessR128, M = root.MasteringLimiter, pre = 0;
            if (L && L.measureIntegratedLUFSAsync) {
                var lufs = await L.measureIntegratedLUFSAsync(out);
                if (isFinite(lufs)) pre = Math.max(-12, Math.min(12, target - lufs));
            }
            tm.loud = performance.now();
            if (M && M.createStreamLimiter) {
                // IN PLACE, block by block. limitAsync kept its own full-length arrays next to the master
                // (the true-peak envelope, the required and the minimum gain — 3 × N floats) and handed
                // back a second master to copy in: +285 MB on a 5-min mix at 48 kHz, the export's peak on
                // a phone (bug report 2026-09-28 — 1.75 GB with three songs). The stream limiter is the
                // same limit() over 4-s blocks with a halo — bit-identical on transients, 1 ULP on dynamic
                // material (mastering-limiter.js) — and holds one block. A block written back lands behind
                // the read position: the stream copied those samples in before it emitted them. `emit`
                // hands the block over as a view of the limiter's scratch — a fresh copy of every block
                // was another 114 MB of garbage at the same peak.
                var d0 = out.getChannelData(0), d1 = out.getChannelData(1), N = out.length, wpos = 0, STEP = sr * 4;
                var write = function (v, n) { d0.set(v[0], wpos); d1.set(v[1], wpos); wpos += n; };
                // blocks that still come back (a limiter from before `emit`) are written the same way
                var put = function (blocks) { blocks.forEach(function (b) { write(b, b[0].length); }); };
                var lim = M.createStreamLimiter(sr, 2, { ceilingDb: -1, preGainDb: pre, emit: write });
                for (var pos = 0; pos < N; pos += STEP) {
                    var e = Math.min(N, pos + STEP);
                    put(lim.push([d0.subarray(pos, e), d1.subarray(pos, e)]));
                    if (ro.onProgress) ro.onProgress(0.6 + 0.4 * e / N);
                    await (root.__yieldTick ? root.__yieldTick(0) : new Promise(function (r) { setTimeout(r, 0); }));
                }
                var tail = lim.flush();
                put(tail.blocks);
                out.__limit = { preGainDb: Math.round(pre * 10) / 10, maxGrDb: tail.maxGrDb };
            } else if (M && M.limitAsync) {
                var chans = [out.getChannelData(0), out.getChannelData(1)];
                var res = await M.limitAsync(chans, sr, { ceilingDb: -1, preGainDb: pre }, function (p) { if (ro.onProgress) ro.onProgress(0.6 + 0.4 * p); });
                var outCh = res.out || chans;
                for (var c = 0; c < 2; c++) if (outCh[c] !== chans[c]) out.getChannelData(c).set(outCh[c]);
                out.__limit = { preGainDb: Math.round(pre * 10) / 10, maxGrDb: res.maxGrDb };
            } else if (pre) {
                var lin = Math.pow(10, pre / 20);
                for (var c2 = 0; c2 < 2; c2++) { var d = out.getChannelData(c2); for (var i = 0; i < d.length; i++) d[i] = Math.max(-0.98, Math.min(0.98, d[i] * lin)); }
            }
            tm.limit = performance.now();
            out.__timing = { render: Math.round(tm.render - tm.t0), loud: Math.round(tm.loud - tm.render), limit: Math.round(tm.limit - tm.loud) };
            if (ro.onProgress) ro.onProgress(1);
            return out;
        }

        return {
            // «was playing» includes a play still waiting for the device: new tracks must not swallow it
            setTracks: function (t) { var was = playing || !!pending, p = position(); pause(); tracks = t; version++; t0Pos = Math.min(p, t.duration); if (was) play(t0Pos); },
            version: function () { return version; },
            set: set, play: play, pause: pause, position: position,
            isPlaying: function () { return playing || !!pending; },
            seek: function (t) { if (playing || pending) play(t); else t0Pos = Math.max(0, Math.min(t, tracks.duration)); if (tickFn) tickFn(t0Pos); },
            setLoop: function (a, b) { loop = (a != null && b > a) ? [a, b] : null; },
            onTick: function (fn) { tickFn = fn; }, onEnd: function (fn) { endFn = fn; },
            render: render, state: function () { return st; }, duration: function () { return tracks.duration; },
            destroy: function () { pause(); },
            // the device was lost and __liveAudioCtxReady gave a NEW context: the old one is dead with
            // every node on it; AudioBuffers carry over, the graph and the worklet are rebuilt on play
            setContext: function (c) { var was = playing || !!pending, p = position(); pause(); ctx = c; workletReady = null; t0Pos = p; if (was) play(p); }
        };
    }

    root.MashupMixer = { create: create, plateIR: plateIR, clipsOf: clipsOf, layTrack: layTrack, trackEnd: trackEnd };
})(typeof window !== 'undefined' ? window : self);
